import { BackendPaymentMethodEnum, PaymentOptionsEnum } from '@/types/api/orders.types';
import { FunctionalComponent, HTMLAttributes, VNodeProps } from 'vue';
import { OrgRoles, ProjRoles, SiteRoles } from '@/types/api/roles.types';
import { PaymentDetails } from '@/types/api/accounts-payable.types';
import { Status } from '@/common/constants';

export type Organisation = {
  id: string;
  name: string;
  email: string;
  contactNumber: string;
  contactNumberCountryCode: string;
  companyVat: string;
  companyRegistration: string;
  companyNhbrc: string;
  address: Address;
  vatDocUrl: DocDetails;
  proofOfAddressDocUrl: DocDetails;
  taxYearEnd: string;
  customData: {
    nhbrc: string;
    contactNumberCountryCode: string;
    vatDocumentKey: string;
    poaDocumentKey: string;
    taxYearEnd: string;
    accountStatementPeriodStart: string;
    accountStatementPeriodEnd: string;
    accountPayrunDate: string;
  };
  logoUrl: string;
  logoName: string;
  accountsStatementPeriodStart: number;
  accountsStatementPeriodEnd: number;
  accountPayrunDay: number;
} & {
  [key: string]:
    | string
    | Address
    | DocDetails
    | {
        taxYearEnd: string;
      }
    | null;
};

export type UsersByRoleCodeResponse = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type CreateOrgBody = {
  name: string;
  email: string;
  contactNumber: string;
  contactNumberCountryCode: string;
  companyVat?: string;
  companyRegistration?: string;
  companyNhbrc?: string;
  address?: Address;
};

export type OrgDocsBody = {
  id: string;
  body: FormData;
};

export type DeleteDocsBody = {
  id: string;
  key: string;
};

export type Address = {
  [key: string]: string | undefined;
  name: string;
  line1: string;
  line2: string;
  line3?: string;
  city: string;
  stateProvince: string;
  country: string;
  countryCode: string;
  postalCode: string;
  buildingNo?: string;
  addressLine1?: string;
  suburb?: string;
};

export type DocDetails = {
  url: string;
  expiresAt: string;
};

export type OrgUser = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  /**
  @deprecated legacy single-role display name — migrate to `roles[]` in PE-354
  */
  role: string;
  /**
  @deprecated legacy single-role code — migrate to `roles[]` in PE-354
  */
  roleCode: OrgRoles;
  /**
   * Multi-role shape from the org-users list endpoint. Optional until the BE
   * makes it unconditional (PE-542); FE readers fall back to the legacy `role`/
   * `roleCode` fields when it is absent. PE-354 migrates the Org Users UI onto this field.
   */
  roles?: { name: string; code: OrgRoles }[];
  invitePending: boolean;
};

export type PaginationParams = {
  pageNo?: number;
  pageSize?: number;
  orderBy?: string;
  orderByDesc?: string;
};

export type ProjectParams = PaginationParams & {
  id?: string;
  statusId?: string;
  archived?: boolean;
  search?: string;
  managerIds?: string;
  status?: string;
};

export type OrgSiteParams = PaginationParams & {
  boqStatus?: string | null;
  projectId?: string;
  siteShortCode?: string;
};

export type OrgSite = {
  id: string;
  projectId: string;
  name: string;
  siteShortCode?: string;
};

// Widened to the full org-role set (was 'ORG_MEMB' | 'ORG_ADMIN' | 'ORG_OWN',
// which omitted ORG_FO / ORG_FA). Aliased to OrgRoles so all five are valid.
export type UserRole = OrgRoles;

export type PRItem = {
  id: string;
  name: string;
};

export type BudgetItem = {
  icon: FunctionalComponent<HTMLAttributes & VNodeProps>;
  label: string;
  secondaryLabel?: string;
  isDoubleValueTile?: boolean;
  title?: string;
  value: number;
  iconClasses: {
    bg: string;
    text: string;
  };
  notCurrencyAmount?: boolean;
  secondaryValue?: string;
  loader?: string;
};

export type AccountsParams = PaginationParams & {
  paymentRequestNumber?: string;
  vendor?: string;
  vendorId?: string | null;
  siteId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  type?: string | null;
  paymentMethod?: string | null;
  isPushedBack?: boolean;
};

export type Order = {
  id: string;
  orderNumber: string;
  deliveryDate: string;
  notes: string;
  amount?: number;
};

export enum ReviewStage {
  Verification = 'Verification',
  Approval = 'Approval',
  Cancellation = 'Cancellation',
  PushBack = 'PushBack',
}

export interface Review {
  reviewedBy: string;
  reviewedByName?: string;
  roleCode: string;
  result: string;
  declineReason: string;
  reviewedAt?: string;
  notes?: string;
  stage?: ReviewStage;
  cancelledFromStatus?: string;
}

export type PRReturn = {
  createdAt: string;
  returnId: string;
  returnNumber: string;
  returnStatus: string;
  returnType: string;
};

/**
 * What a payment request's `referenceEntity` points at. The wire carries other sources too, so
 * `PRReferenceEntity.source` stays a string and these are only the ones the drawer acts on.
 */
export const PRReferenceSource = {
  ORDER: 'Order',
  BULK_ORDER: 'BulkOrder',
  RETURN: 'Return',
} as const;

export type PRReferenceEntity = {
  id: string;
  requestNumber?: string;
  returnNumber?: string;
  source: string;
};

export type PRChangeLogItem = {
  amount: number;
  amountInclVat: number;
  createdAt: string;
  createdById: string;
  createdByName: string;
  description: string;
  type: string;
  referenceEntity: PRReferenceEntity;
};

/**
 * The payment that paid a request, as the request detail returns it (PE-1021). Money is shown as
 * given: nothing on the request side is computed from `amount`.
 */
export type PaymentLogged = {
  paymentId: string;
  reference: string;
  amount: number;
  date: string;
  status: 'AwaitingProof' | 'Confirmed';
  blocked: boolean;
  settledMoreThanThisRequest: boolean;
  otherRequestsSettledCount: number;
};

/**
The bulk order a bulk payment request settles; null on every ordinary request (PE-952).
*/
export type PaymentRequestBulkOrder = { id: string; reference: string };

/**
One site order a bulk payment request covers, in the backend's shape (PE-952).

`shareInclVat` is served and typed but never rendered: the drawer values each site by its own order
total, never by its share of the request (DL-232).
*/
export type PaymentRequestLinkedOrder = {
  id: string;
  orderNumber: string;
  deliveryDate: string;
  notes: string;
  amount: number;
  siteId?: string;
  shareInclVat?: number;
};

/**
 * One site order of a bulk payment request's set, as the detail read serves it (PE-1169's
 * `Contract:` comment, 2026-10-12). `status` is the order status name; `isDelivered` is the same
 * test the API's order-status rule applies and `isLive` says whether the row counts. Every figure
 * is incl VAT, and `paidInclVat` can read below zero in a rare case (DL-235 D-01).
 *
 * Context: the drawer shows `owedInclVat` as the site order's value. `paidInclVat` is served and
 * typed but not shown, until partial payments are supported (the product owner, 2026-10-14).
 */
export type BulkBreakdownSite = {
  siteId: string;
  siteName: string;
  orderId: string;
  orderNumber: string;
  status: string;
  isDelivered: boolean;
  isLive: boolean;
  owedInclVat: number;
  paidInclVat: number;
};

/**
 * Only `owedInclVat`, the bulk order's total, is shown. The rest is served and typed but not shown,
 * until partial payments are supported (the product owner, 2026-10-14).
 */
export type BulkBreakdownTotals = {
  owedInclVat: number;
  paidInclVat: number;
  requestAmountInclVat: number;
  stillToPayInclVat: number;
  otherRequestUnpaid: boolean;
  /**
   * A request of the bulk order's set is Paid: the API's own test for refusing a Decline
   * (`PaidInSetReason`), served so the drawer never infers it from an amount.
   */
  anyRequestPaid: boolean;
};

/**
 * What a bulk payment request's drawer lists per site. Served on `GET /payments-requests/{id}` only,
 * and absent (not null) on every other request and on every `PATCH` response, so it is read on open
 * and again after an action (PE-1175).
 */
export type PaymentRequestBulkBreakdown = {
  /**
  False on a deposit request and on a cash-on-delivery payment request, which serve no delivery rule.
  */
  rulesIncludeDelivery: boolean;
  sites: BulkBreakdownSite[];
  totals: BulkBreakdownTotals;
};

export type PaymentRequest = {
  id: string;
  paymentRequestNumber: string;
  accountsPayableId: string;
  date: string;
  siteId: string;
  siteName: string;
  projectId: string;
  organisationId: string;
  boqId: string;
  vendorId: string;
  vendorName: string;
  vendorEmail: string;
  vendorAccNumber: string;
  // Context: a bulk request settles many site orders, so it has none of its own (`order: null`).
  order?: Order | null;
  bulkOrder?: PaymentRequestBulkOrder | null;
  linkedOrders?: PaymentRequestLinkedOrder[] | null;
  bulkBreakdown?: PaymentRequestBulkBreakdown;
  status: Status;
  amount: number;
  amountInclVat: number;
  initialAmount: number;
  type: PaymentRequestTypeEnum;
  reviews: Review[];
  returns: PRReturn[];
  notes?: string | null;
  changelog: PRChangeLogItem[];
  paymentDetails: PaymentDetails | null;
  sharedDocuments: PRDocument[];
  adjustmentInformation: AdjustmentInformation | null;
  referenceEntity?: PRReferenceEntity[];
  /**
   * Present on the detail read only, and only with the lifecycle flag on and payment view held:
   * absent otherwise (not null), and never on a PATCH response.
   */
  paymentLogged?: PaymentLogged | null;
  paymentLoggedUnavailable?: boolean;
  /**
   * The payment awaiting its proof that holds this request, on the list read only, with the
   * lifecycle flag on and payment view held: absent otherwise. A held request cannot be logged
   * again until that payment confirms or is cancelled.
   */
  awaitingProofPayment?: PaymentRequestAwaitingProof | null;
};

export type PaymentRequestAwaitingProof = {
  paymentId: string;
  paymentNumber: string;
  /** Its proof arrived and was blocked at confirm, so it reads as blocked, not awaiting. */
  blocked: boolean;
};

export type Payment = {
  id: string;
  date: string;
  amount: number;
  amountPaid: number;
  creditApplied: number;
  paymentRequests: Partial<PaymentRequest>[];
};

export enum APRTypeEnum {
  LOG_DELIVERY = 'LogDelivery',
  RETURN_CANCELLED = 'ReturnCancelled',
  RETURN_DECLINED = 'ReturnDeclined',
}

export enum PaymentRequestTypeEnum {
  OPR = 'Order Payment Request',
  CPR = 'Claim Payment Request',
  DEP = 'Deposit Request',
  ADV = 'Advanced Payment Request',
  RET = 'Retention',
  APR = 'Adjustment Payment Request',
}

export type AdjustmentInformation = {
  reason: string;
  type: APRTypeEnum;
};

export type VendorAccountSummary = {
  id: string;
  name: string;
  path: string;
  vendorNotified: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  startDate: string;
  endDate: string;
  dateRange?: string;
};

export type AccountPayableTotals = {
  amountOwed: number;
  balance: number;
  credit: number;
  paidTotal: number;
  pendingTotal: number;
};

export type AccountPayable = {
  id: string;
  organisationId: string;
  vendorId: string;
  vendorName: string;
  totalApproved: number;
  pendingApprovals: number;
  credit?: number;
  dueNow: number;
  nextDueDate?: string;
  status: string;
  paymentRequests: Partial<PaymentRequest>[];
  payments: Payment[];
  summaries: VendorAccountSummary[];
  createdAt: string;
  totals?: AccountPayableTotals;
};

export type APPeriod = {
  id: number;
  enum: string;
  name: string;
  description: string;
};

export type APAssignee = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  combinedName?: string;
};

export type APPaymentMethodSelectTypes = {
  enum:
    | PaymentOptionsEnum.DEPOSIT
    | PaymentOptionsEnum.ACCOUNT
    | BackendPaymentMethodEnum.ACCOUNT
    | BackendPaymentMethodEnum.COD;
  name: string;
};

export type APPeriodData = string[];

export type AccountsPayableTotals = {
  approvedCodDueNow: number;
  approvedCodToMonthEnd: number;
  approvedAccountDueNext: number;
  pendingPayments: number;
  totalCredit: number;
  pendingPaymentRequests: number;
};

export type PaymentRequestTotals = {
  currentTotal: number;
  thirtyDayTotal: number;
  sixtyDayTotal: number;
  ninetyDayTotal: number;
};

export type OrderVendor = {
  vendorId: string;
  vendorName: string;
};

export type AccountsTableItem = AccountPayable & PaymentRequest;

export type NewOrderItem = {
  existsInBoq: boolean;
  lineItemPath: number[];
  lineItemInGroup: boolean;
  name: string;
  specification: string;
  vendorItemId: string;
  rate: number;
  quantity: number;
};

export type CreateOrgOrder = {
  siteId: string;
  deliveryDate: string;
  notes: string;
  items: NewOrderItem[];
  paymentInformation: PaymentInformation[];
};

export type PaymentInformation = {
  vendorId: string;
  /**
   * The reworked flows send the deposit's `amount` incl VAT, as their Payments step showed it. The
   * legacy create-order screen (`CreateOrderRequests.vue`) still sends `percent`, which the API
   * keeps for it; never both.
   */
  deposit: {
    amount?: number | null;
    percent?: number | null;
    dueDate: string | null;
    assigneeId: string | null;
  };
  retentions?: {
    percent: number;
    dueDate: string;
    assigneeId: string;
  }[];
  order: {
    method: string;
    dueDate?: string;
    assigneeId?: string;
  };
};

export type OrganisationProjectListForUser = {
  projects: OrganisationProjectList[];
};

export type OrganisationProjectList = {
  id: string;
  name: string;
  roles: ProjRoles[];
  sites: OrganisationSiteList[];
};

export type OrganisationSiteList = {
  id: string;
  name: string;
  roles: SiteRoles[];
};

export type PRDocument = {
  documentType: string;
  id: string;
  name: string;
  path: string;
  sourceEntity: string;
  sourceEntityId: string;
};

export interface DocumentDetailsResponse {
  data: {
    id: string;
    name: string;
    url: string;
    urlExpiry: string;
  };
}
